import { enumMembers, type ApiSchema } from "../../apiSchema";
import { jsonBody, requestJson, withQuery } from "../../http/httpClient";
import { readArray, readInteger, readNullableInteger, readNullableString, readObject, readOneOf, readString } from "../../http/jsonDecoding";
import { readShapeViolations, type ShapeViolations } from "../mergeBlockDecoders";
import { ITEM_KINDS, type ItemKind } from "../reviewDecoders";
import { readStepPlan, type StepPlan } from "../stepPlans";
import { toUiError } from "../../../errors/uiError";
import { decodeProcessItemContent, decodeWorkflowInputDeclaration, type ProcessItemContent, type WorkflowInputDeclaration } from "./processContentDecoders";
import { decodeRecord, oneOf } from "./selectorDecoders";

/**
 * The reads and checks the authoring forms need from module B (authoring-ui.md AU-3,
 * AU-9): one item with its whole definition, the project's items at a version, and the
 * definition check a commit runs, without committing.
 */

/** One project item at one exact version, with the definition the forms start from. */
export interface AuthoredItem {
  readonly reference: string;
  readonly itemId: string;
  readonly kind: ItemKind;
  readonly name: string;
  /** `null` for kinds the forms do not read (ontology modules, shapes, questions, examples). */
  readonly content: ProcessItemContent | null;
  /** Artifacts only: the change set, Thread and Workflow that made this version. */
  readonly madeBy: ItemMadeBy | null;
}

export interface ItemMadeBy {
  readonly changeSetId: string;
  readonly thread: string | null;
  readonly workflow: string | null;
  /** `<thread id>#<entry number>` each. */
  readonly originEntries: readonly string[];
}

/** One problem a definition check found, at a dotted path into the definition (W-12). */
export interface DefinitionProblem {
  readonly path: string;
  /** Module B's own words: a rule's sentence, or for a field check its validator's English, never shown (TB2-02). */
  readonly message: string;
  /** The field check that failed, such as `missing` or `too_short`; `null` for a rule. */
  readonly check: string | null;
  /** A length check's bound. */
  readonly limit: number | null;
}

export interface ListedProjectItem {
  readonly kind: ItemKind;
  readonly itemId: string;
  readonly name: string;
  readonly reference: string;
}

export interface ProjectItemListing {
  /** The commit listed; `null` before the project's first change set. */
  readonly version: string | null;
  readonly items: readonly ListedProjectItem[];
}

const LISTING_PAGE_SIZE = 100;

export function readAuthoredItem(reference: string, signal?: AbortSignal): Promise<AuthoredItem> {
  return requestJson({
    method: "GET",
    path: `/content/items/${encodeURIComponent(reference).replaceAll("%40", "@")}`,
    signal,
    decodeResponse: (body) => {
      const item = readObject(body, "item version");
      const itemReference = readString(item, "reference");
      const kind = readOneOf(item, "kind", ITEM_KINDS);
      return {
        reference: itemReference,
        itemId: itemReference.split("@")[0] ?? itemReference,
        kind,
        name: readString(item, "name"),
        content: decodeProcessItemContent(kind, item["definition"]),
        madeBy: decodeMadeBy(item["made_by"]),
      };
    },
  });
}

/** Who or what made an artifact version (P-13); `null` if no merge from a Thread brought it. */
export function decodeMadeBy(value: unknown): ItemMadeBy | null {
  if (value === null || value === undefined) {
    return null;
  }
  const madeBy = readObject(value, "made by");
  const optional = (member: string) => (typeof madeBy[member] === "string" ? madeBy[member] : null);
  return {
    changeSetId: readString(madeBy, "change_set_id"),
    thread: optional("thread"),
    workflow: optional("workflow"),
    originEntries: readArray(madeBy["origin_entries"] ?? [], "origin entries", (entry) => readString({ entry }, "entry")),
  };
}

/** Every item of the project at a commit (a change set's head), or at `main` with `null`. */
export async function listProjectItems(projectId: string, commit: string | null, signal?: AbortSignal): Promise<ProjectItemListing> {
  try {
    return await readProjectItemPages(projectId, commit, signal);
  } catch (caught: unknown) {
    const error = toUiError(caught);
    if (commit === null && error.kind === "problem" && error.problem.code === "resource_not_found" && error.problem.details["resource_kind"] === "project_content") {
      return { version: null, items: [] };
    }
    throw caught;
  }
}

async function readProjectItemPages(projectId: string, commit: string | null, signal?: AbortSignal): Promise<ProjectItemListing> {
  const items: ListedProjectItem[] = [];
  for (let offset = 0; ; offset += LISTING_PAGE_SIZE) {
    const page = await requestJson({
      method: "GET",
      path: withQuery(`/projects/${encodeURIComponent(projectId)}/content/items`, { version: commit, offset, limit: LISTING_PAGE_SIZE }),
      signal,
      decodeResponse: (body) => {
        const listing = readObject(body, "item listing");
        return {
          version: readString(listing, "version"),
          total: readInteger(listing, "total_item_count"),
          items: readArray(listing["items"], "items", (value): ListedProjectItem => {
            const entry = readObject(value, "item");
            return {
              kind: readOneOf(entry, "kind", ITEM_KINDS),
              itemId: readString(entry, "item_id"),
              name: readString(entry, "name"),
              reference: readString(entry, "reference"),
            };
          }),
        };
      },
    });
    items.push(...page.items);
    if (page.items.length === 0 || offset + LISTING_PAGE_SIZE >= page.total) {
      return { version: page.version, items };
    }
  }
}

/** The process kinds a definition check reads (W-12). */
export type ProcessContentBody = ApiSchema<"ProcessDefinitionBody">;

/** The server's verdict on a draft: what a commit would refuse, and on request what the review's shapes say. */
export interface DefinitionValidation {
  readonly problems: readonly DefinitionProblem[];
  /** With `checkShapes`, for a process item without problems; `null` otherwise (reviews.md R-3). */
  readonly shapes: ShapeViolations | null;
  /** For a Workflow draft that parses and flattens, problems or not: module B's plan of each flat step before anything ran, by step key; `null` when nothing is known (WD-1+3 D-6). */
  readonly plans: ReadonlyMap<string, StepPlan> | null;
  /** For the same draft: the inputs it stores once one of them has a default, as module B merges them; an editor writes this list and changes only an entry's default (WD-1+3 S4-G1). */
  readonly declaredInputs: readonly WorkflowInputDeclaration[] | null;
  /** For the same draft: what fills each input slot of each step, by step key, then slot, as module B says (F102 CP-04). */
  readonly inputFills: ReadonlyMap<string, Readonly<Record<string, InputFill>>> | null;
}

/** What fills a step's input slot in its Workflow: an earlier step's output, the fork point, a Workflow input, a default, or nothing. */
export type InputFill = ApiSchema<"InputFill">;
const INPUT_FILLS = enumMembers<InputFill>({ step_output: true, fork_point: true, workflow_input: true, default: true, missing: true });

/**
 * Every problem saving `content` as item `itemId` (a new one with `null`) on top of `main`
 * would report; empty if none. With `checkShapes`, a draft without problems is also checked
 * with the shapes the review runs, split as a merge splits them. Commits nothing (AU-3, AU-9).
 */
export function validateDefinition(
  projectId: string,
  content: ProcessContentBody,
  itemId: string | null,
  { checkShapes = false, signal }: { readonly checkShapes?: boolean; readonly signal?: AbortSignal } = {},
): Promise<DefinitionValidation> {
  return requestJson({
    method: "POST",
    path: `/projects/${encodeURIComponent(projectId)}/content/definitions/validate`,
    body: jsonBody({ content, item_id: itemId, check_shapes: checkShapes } satisfies ApiSchema<"DefinitionValidationBody">),
    signal,
    decodeResponse: (body) => {
      const result = readObject(body, "validation result");
      const shapes = result["shapes"];
      const plans = result["plans"];
      const declared = result["declared_inputs"];
      const fills = result["input_fills"];
      return {
        problems: readArray(result["problems"], "problems", (value) => {
          const problem = readObject(value, "problem");
          return { path: readString(problem, "path"), message: readString(problem, "message"), check: readNullableString(problem, "check"), limit: readNullableInteger(problem, "limit") };
        }),
        shapes: shapes === undefined || shapes === null ? null : readShapeViolations(shapes),
        plans: plans === undefined || plans === null ? null : new Map(readArray(plans, "step plans", readStepPlan).map((plan) => [plan.stepKey, plan])),
        declaredInputs: declared === undefined || declared === null ? null : readArray(declared, "declared inputs", decodeWorkflowInputDeclaration),
        inputFills:
          fills === undefined || fills === null
            ? null
            : new Map(Object.entries(decodeRecord(fills, "input fills", (slots) => decodeRecord(slots, "slot fills", (fill) => oneOf(fill, INPUT_FILLS, "input fill"))))),
      };
    },
  });
}
